import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { StatusBadge } from '../../utils/StatusBadge';
import GetCookie from '../../utils/GetCookie';
import ErrorPopup from '../../utils/popup/ErrorPopup';
import ProcessingPopup from '../../utils/popup/ProcessingPopup';
import SuccessPopup from '../../utils/popup/SuccessPopup';
import DocumentPreview from '../../utils/DocumentPreview';
import DeleteConfirmationModal from '../../utils/DeleteConfirmationModal';
import RetriggerConfirmationModal from '../../utils/RetriggerConfirmationModal';
import TrimContent from '../../utils/TrimContent';
import ReadMoreTruncateText from '../../utils/ReadMoreTruncateText';
import PaginationControls from '../../utils/PaginationControls';
import { useNavigate } from "react-router-dom";
import authNavigate from '../../globalComponents/AuthNav';
import AILegalityBadge from './AILegalityBadge';

const CheckBoxForDocSelect = ({doc_id, checked, onChange }) => {
 
  const handleCheckboxChange = (event) => {
    onChange(doc_id, event.target.checked);
  }
  
  return (
    <input type="checkbox" checked={checked} onChange={handleCheckboxChange} />
  );
};

const MetadataEditor = ({ 
  threadId, 
  documents, 
  metadataList,
  metadataTotal,
  page,
  size,
  setSize,
  search,
  sortField,
  sortDirection,
  onDocumentsUpdated, 
  onPreviewDocument,
  onPageChange,
  onSizeChange,
  onSearchChange,
  onSortChange,
  onRefresh,
  editingRows,
  setEditingRows,
  editedMetadata,
  setEditedMetadata,
  hasGlobalChanges,
  setHasGlobalChanges,
  setIsProcessingPopupVisible,
  setProcessingMessage,
  setIsErrorPopupVisible,
  setPopupErrorMessage,
  setIsSuccessPopupVisible,
  setPopupSuccessMessage,
  threadTags = []
}) => {
  const [searchInput, setSearchInput] = useState("");

  const navigate = useNavigate();

  const API_BASE_URL = "/pceapi/threads/unstructured";
  const headers = {
    Authorization: GetCookie("access_token"),
  };

  // Initialize searchInput with search value
  useEffect(() => {
    setSearchInput(search);
  }, [search]);

  // Handle sort change
  const handleSortChange = (field) => {
    onSortChange(field);
  };

  // Render sort arrow
  const renderSortArrow = (field) => {
    if (sortField !== field) return null;
    return (
      <span className="ms-1">
        {sortDirection === "asc" ? (
          <i className="bi bi-caret-up-fill"></i>
        ) : (
          <i className="bi bi-caret-down-fill"></i>
        )}
      </span>
    );
  };

  // Handle edit button click
  const handleEditRow = (docId) => {
    setEditingRows(new Set([...editingRows, docId]));
  };

  // Handle cancel edit
  const handleCancelEdit = (docId) => {
    const newEditingRows = new Set(editingRows);
    newEditingRows.delete(docId);
    setEditingRows(newEditingRows);
    
    // Remove any unsaved changes for this document
    const newEditedMetadata = { ...editedMetadata };
    delete newEditedMetadata[docId];
    setEditedMetadata(newEditedMetadata);
    
    // Update hasGlobalChanges
    setHasGlobalChanges(Object.keys(newEditedMetadata).length > 0);
  };

  // Handle metadata field change
  const handleMetadataChange = (docId, fieldName, value) => {
    if (!editedMetadata[docId]) {
      editedMetadata[docId] = {};
    }
    
    // For array fields, store as array but allow editing as comma-separated string
    editedMetadata[docId][fieldName] = value;
    
    setEditedMetadata({...editedMetadata});
    setHasGlobalChanges(true);
  };

  // Save individual row changes (only update state, no API call)
  const saveRowChanges = (docId) => {
    if (!editedMetadata[docId]) return;

    // Remove from editing state but keep changes staged
    const newEditingRows = new Set(editingRows);
    newEditingRows.delete(docId);
    setEditingRows(newEditingRows);
    
    // Keep the edited metadata for global save
    setHasGlobalChanges(Object.keys(editedMetadata).length > 0);
  };

  // Save all metadata changes (master save with API call)
  const saveAllChanges = async () => {
    if (!Object.keys(editedMetadata).length) return;

    setIsProcessingPopupVisible(true);
    setProcessingMessage("Saving all metadata changes");


    try {
       // Parse array fields before sending to API
    const processedMetadata = {};

    Object.entries(editedMetadata).forEach(([docId, docMetadata]) => {
      processedMetadata[docId] = {};

      Object.entries(docMetadata).forEach(([fieldName, value]) => {
        if (fieldName === 'authors' || fieldName === 'keywords') {
          // Convert comma-separated string to array
          processedMetadata[docId][fieldName] = parseArrayField(value);
        } else {
          processedMetadata[docId][fieldName] = value;
        }
      });
    });
      const response = await axios.post(
        `${API_BASE_URL}/update-metadata`,
        {
          thread_id: threadId,
          metadata: processedMetadata,
        },
        { headers }
      );

      const data = response.data;

      if (data.status === "NotAuthorized") {
        authNavigate('/login', 'iam', navigate);
        return;
      }

      if (data.status === "failed") {
        setPopupErrorMessage(data.message || "Failed to save metadata");
        setIsErrorPopupVisible(true);
        return;
      }

      setPopupSuccessMessage("All metadata saved successfully");
      setIsSuccessPopupVisible(true);
      
      // Clear all editing states
      setEditingRows(new Set());
      setEditedMetadata({});
      setHasGlobalChanges(false);

      // Refresh data
      onRefresh();

    } catch (error) {
      setPopupErrorMessage(error.message || "Failed to save metadata");
      setIsErrorPopupVisible(true);
    } finally {
      setIsProcessingPopupVisible(false);
    }
  };

  // Handle search
  const handleSearch = () => {
    onSearchChange(searchInput);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const formatArrayField = (value) => {
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    return value || '';
  };

  const parseArrayField = (value) => {
    if (typeof value === 'string') {
      return value.split(',').map(item => item.trim()).filter(item => item);
    }
    return value;
  };

  // Get field value (either from edited metadata or original metadata)
  const getFieldValue = (item, fieldName) => {
    const docId = item.doc_id;
    
    // Check if there's an edited value
    if (editedMetadata[docId] && editedMetadata[docId][fieldName] !== undefined) {
      const editedValue = editedMetadata[docId][fieldName];
      // For display purposes, format arrays as comma-separated strings
      if (fieldName === 'authors' || fieldName === 'keywords') {
        return formatArrayField(editedValue);
      }
      return editedValue;
    }
    
    // Return original value from metadata
    const originalValue = item.metadata?.[fieldName];
    
    // For display purposes, format arrays as comma-separated strings
    if (fieldName === 'authors' || fieldName === 'keywords') {
      return formatArrayField(originalValue);
    }
    
    return originalValue || "";
  };

  // Render editable field - All fields are now textareas
  const renderEditableField = (item, fieldName, useReadMore = false, readMoreLimit = 50) => {
    const docId = item.doc_id;
    const isEditing = editingRows.has(docId);
    const value = getFieldValue(item, fieldName);
    
    if (!isEditing) {
      if (useReadMore && value && value.length > readMoreLimit) {
        return (
          <div >
               <ReadMoreTruncateText
                text={value}
                 limit={readMoreLimit}
                />
          </div>
        );
      }
      
      return (
        <div className="ev-small-p-dark">
          {value || <span className="ev-small-p-dark">Not set</span>}
        </div>
      );
    }
    
    // Classification field: custom dropdown with thread tags + unclassified
    if (fieldName === 'classification' && threadTags.length > 0) {
      const allOptions = [...threadTags, 'unclassified'];
      const isDropOpen = editedMetadata[docId]?._tagDropOpen;
      const toggleDrop = () => {
        setEditedMetadata(prev => ({
          ...prev,
          [docId]: { ...prev[docId], _tagDropOpen: !isDropOpen }
        }));
      };
      const closeDrop = (selectedTag) => {
        if (selectedTag) handleMetadataChange(docId, fieldName, selectedTag);
        setEditedMetadata(prev => ({
          ...prev,
          [docId]: { ...prev[docId], _tagDropOpen: false }
        }));
      };
      return (
        <>
          <button
            type="button"
            className="ev-message-card4 border-0 d-flex align-items-center gap-1 ev-fs-11 w-100"
            onClick={toggleDrop}
            id={`tag-btn-${docId}`}
          >
            {value || 'Select tag'}
            <i className={`bi bi-chevron-${isDropOpen ? 'up' : 'down'} ev-fs-9`}></i>
          </button>
          {isDropOpen && createPortal(
            <div
              className="position-fixed top-0 start-0 w-100 h-100"
              style={{ zIndex: 'var(--num-100000000)' }}
              onClick={() => closeDrop(null)}
            >
              <div
                className="position-absolute ev-card-4 ev-message-card2 d-flex flex-column gap-1 p-2 rounded-2 section-type-dropdown-panel shadow"
                style={{
                  zIndex: 'var(--num-100000000)',
                  minWidth: '220px',
                  top: (() => { const r = document.getElementById(`tag-btn-${docId}`)?.getBoundingClientRect(); return r ? r.bottom + 4 + 'px' : '0'; })(),
                  right: (() => { const r = document.getElementById(`tag-btn-${docId}`)?.getBoundingClientRect(); return r ? (window.innerWidth - r.right) + 'px' : '0'; })()
                }}
                onClick={e => e.stopPropagation()}
              >
                <span className="ev-small-p-blue ev-bold-med px-1 mb-1 text-start">Document Tag</span>
                {allOptions.map((tag, idx) => (
                  <button
                    key={idx}
                    type="button"
                    className={`par-style-item d-flex align-items-center ev-fs-12 py-1 px-2 ${(value || '').toLowerCase() === tag.toLowerCase() ? 'active' : ''}`}
                    onClick={() => closeDrop(tag)}
                  >
                    <i className={`bi ${(value || '').toLowerCase() === tag.toLowerCase() ? 'bi-check-circle-fill' : 'bi-circle'} me-2`}></i>
                    {tag}
                  </button>
                ))}
              </div>
            </div>,
            document.body
          )}
        </>
      );
    }

    // All other editable fields are textareas with auto-resize
    return (
      <textarea
        className="form-control formField3 scrollable-element vh-10"
        value={value}
        onChange={(e) => handleMetadataChange(docId, fieldName, e.target.value)}
      />
    );
  };

  // Pagination logic
  const totalPages = Math.ceil(metadataTotal / size);
  const paginationItems = [];
  const maxPagesToShow = 5;
  let startPage = Math.max(1, page - Math.floor(maxPagesToShow / 2));
  let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

  if (endPage - startPage + 1 < maxPagesToShow) {
    startPage = Math.max(1, endPage - maxPagesToShow + 1);
  }

  // Previous button
  paginationItems.push(
    <li key="prev" className={`page-item ${page === 1 ? "disabled" : ""}`}>
      <button
        className="ev-btn-light-fill me-1"
        onClick={() => onPageChange(page - 1)}
        disabled={page === 1}
      >
        <i className="bi bi-chevron-left"></i>
      </button>
    </li>
  );

  // Page numbers
  for (let i = startPage; i <= endPage; i++) {
    paginationItems.push(
      <li key={i} className={`page-item ${page === i ? "active" : ""}`}>
        <button
          className="ev-btn-light-fill me-1"
          onClick={() => onPageChange(i)}
        >
          {i}
        </button>
      </li>
    );
  }

  // Next button
  paginationItems.push(
    <li
      key="next"
      className={`page-item ${page === totalPages ? "disabled" : ""}`}
    >
      <button
        className="ev-btn-light-fill me-1"
        onClick={() => onPageChange(page + 1)}
        disabled={page === totalPages}
      >
        <i className="bi bi-chevron-right"></i>
      </button>
    </li>
  );

  return (
    <div className="metadata-editor">
      <div className="row mt-1 mb-3">
        <div className="col-7 d-flex align-items-center">
          <p className="ev-reg-p-blue mb-0">Document Metadata ({metadataTotal})</p>
          <button
              className="ev-btn-lightblue-fill-round ev-bold-reg ms-2"
              onClick={onRefresh}
            >
              <i className="bi bi-arrow-clockwise"></i> Refresh Metadata
            </button>
          {hasGlobalChanges && (
            <button
              className="ev-btn-yellow-fill-round ev-bold-reg ms-2"
              onClick={saveAllChanges}
            >
              <i className="bi bi-check-circle"></i> Update Metadata
            </button>
          )}
        </div>

        <div className="col-5 position-relative">
          {/* Search form */}
          <input
            type="text"
            className="form-control search-input ms-0"
            placeholder="Search document name"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyPress={handleSearchKeyPress}
          />
          <button
            className="ev-btn-lightblue-fill-round search-bar-button"
            onClick={handleSearch}
          >
            <i className="bi bi-search"></i>
          </button>
        </div>
      </div>

      {metadataList.length === 0 ? (
        <div className="alert alert-primary">
          No metadata found for documents in this Data Collection.
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <div className="table-container">
              <div className="scrollable-element vh-75">
                <table className="table-custom1 table-metadata mt-0 w-100 table-fixed">
                  <colgroup>
                    <col className="col-meta-doc-name" />
                    <col className="col-meta-title" />
                    <col className="col-meta-desc" />
                    <col className="col-meta-class" />
                    <col className="col-meta-publisher" />
                    <col className="col-meta-authors" />
                    <col className="col-meta-organization" />
                    <col className="col-meta-keywords" />
                    <col className="col-meta-year" />
                    <col className="col-meta-jpn" />
                    <col style={{ width: '140px' }} />
                    <col style={{ width: '160px' }} />
                    <col style={{ width: '120px' }} />
                    <col className="col-meta-actions" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th onClick={() => handleSortChange("document_name")}>
                        <p className ="mb-0">Document Name {renderSortArrow("document_name")}</p>
                      </th>
                      <th onClick={() => handleSortChange("title")}>
                        <p className ="mb-0">Title {renderSortArrow("title")}</p>
                      </th>
                      <th>
                        <label>Description</label>
                      </th>
                      <th>
                        <label>Publisher</label>
                      </th>
                      <th>
                        <label>Authors</label>
                      </th>
                      <th>
                        <label>Organization</label>
                      </th>
                      <th>
                        <label>Keywords</label>
                      </th>
                      <th onClick={() => handleSortChange("year")}>
                        <label>Year {renderSortArrow("year")}</label>
                      </th>
                      <th onClick={() => handleSortChange("journal_page_no")}>
                        <label>Journal Page No{renderSortArrow("journal_page_no")}</label>
                      </th>
                      <th>
                        <label>DOI</label>
                      </th>
                      <th>
                        <label>Website URL</label>
                      </th>
                      <th>
                        <label>Accessed Date</label>
                      </th>
                      <th onClick={() => handleSortChange("classification")} className="text-nowrap">
                        <label>Document Tag {renderSortArrow("classification")}</label>
                      </th>
                      <th>
                        <label>Actions</label>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {metadataList.map((item, index) => {
                      const docId = item.doc_id;
                      const isEditing = editingRows.has(docId);
                      const hasChanges = editedMetadata[docId] && Object.keys(editedMetadata[docId]).length > 0;
                      
                      return (
                        <tr key={index}>
                          <td className="align-top avatar-tooltip" data-tooltip={item.document_name || "Unknown"}>
                            <div className="d-flex align-items-center flex-nowrap gap-2">
                              <span className="text-truncate ev-min-w-0">
                                <TrimContent text={item.document_name || "Unknown"} maxLength={20} />
                              </span>
                              <button
                                className="ev-btn-lightblue-fill-round ev-btn-xsmall flex-shrink-0"
                                onClick={() => onPreviewDocument(docId, item.document_name)}
                              > 
                                <i className="bi bi-eye"></i> view
                              </button>
                            </div>
                          </td>
                          <td className="align-top text-break">{renderEditableField(item, "title", true, 50)}</td>
                          <td className="align-top text-break">{renderEditableField(item, "description", true, 100)}</td>
                          <td className="align-top text-break">{renderEditableField(item, "publisher", true, 100)}</td>
                          <td className="align-top">{renderEditableField(item, "authors", true, 50)}</td>
                          <td className="align-top text-break">{renderEditableField(item, "organization", true, 100)}</td>
                          <td className="align-top text-break">{renderEditableField(item, 'keywords',true,50)}</td>
                          <td className="align-top">{renderEditableField(item, "year")}</td>
                          <td className="align-top">{renderEditableField(item, "journal_page_no", true, 50)}</td>
                          <td className="align-top">{renderEditableField(item, "doi", true, 40)}</td>
                          <td className="align-top">{renderEditableField(item, "website_url", true, 40)}</td>
                          <td className="align-top">{renderEditableField(item, "accessed_date", true, 30)}</td>
                          <td className="align-top text-nowrap">{renderEditableField(item, "classification")}</td>
                          <td className="align-top text-center">
                            {!isEditing ? (
                              <button
                              className="btn ev-btn-skyblue-fill-round ev-btn-med ev-radius-xsmall pe-2 ps-2 avatar-tooltip"
                              data-tooltip="Edit Metadata"
                              onClick={() => handleEditRow(docId)}
                            >
                              <i className="bi bi-pencil"></i>
                            </button>
                            ) : (
                              <div className="d-flex flex-row gap-1 flex-nowrap">
                                <button
                                  className="ev-btn-lightblue-fill-round ev-btn-small"
                                  onClick={() => saveRowChanges(docId)}
                                  disabled={!hasChanges}
                                  title="Save"
                                >
                                  <i className="bi bi-check"></i>
                                </button>
                                <button
                                  className="ev-btn-lightblue-fill-round ev-btn-small"
                                  onClick={() => handleCancelEdit(docId)}
                                  title="Cancel"
                                >
                                  <i className="bi bi-x"></i>
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-3">
                <div>
                  <PaginationControls 
                    initialSize={size}
                    onSizeChange={(newSize) => {
                      setSize(newSize);
                      onPageChange(1); // reset page using parent callback
                    }}
                  />
                </div>
              <div className="ev-small-p-dark">
                Showing {Math.min((page - 1) * size + 1, metadataTotal)} to{" "}
                {Math.min(page * size, metadataTotal)} of {metadataTotal} documents
              </div>
              <nav aria-label="Document pagination">
                <ul className="pagination pagination-sm mb-0 me-w">
                  {paginationItems}
                </ul>
              </nav>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const DocumentStatusTable = ({ threadId, onDocumentsUpdated, externalRefreshTrigger = 0 ,onThreadStatusRefresh}) => {
  // State variables for document status table
  const [documents, setDocuments] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(25);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [sortField, setSortField] = useState('document_name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [currentView, setCurrentView] = useState("status");
  const [selectAll, setSelectAll] = useState(false);
  const [activePopoverId, setActivePopoverId] = useState(null);
  // Delete document states
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);

  // Select document states
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [showDeleteSelectedConfirm, setShowDeleteSelectedConfirm] = useState(false);
  
  const [docPercentages,setDocPrecentages] = useState({
    upl_sta:0,stand_sta:0,md_rag_sta:0,img_rag_sta:0,auto_class_sta:0,parquet_sta:0
  })
  
  // Popup states
  const [isProcessingPopupVisible, setIsProcessingPopupVisible] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');
  const [isErrorPopupVisible, setIsErrorPopupVisible] = useState(false);
  const [popupErrorMessage, setPopupErrorMessage] = useState('');
  const [isSuccessPopupVisible, setIsSuccessPopupVisible] = useState(false);
  const [popupSuccessMessage, setPopupSuccessMessage] = useState('');

  // Document preview states
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewDocId, setPreviewDocId] = useState(null);
  const [previewDocumentName, setPreviewDocumentName] = useState(null);
   const [metadataList, setMetadataList] = useState([]);
  const [metadataTotal, setMetadataTotal] = useState(0);
  const [metadataEditingRows, setMetadataEditingRows] = useState(new Set());
  const [metadataEditedData, setMetadataEditedData] = useState({});
  const [metadataHasGlobalChanges, setMetadataHasGlobalChanges] = useState(false);

  const [showRetriggerConfirm, setShowRetriggerConfirm] = useState(false);
  const [retriggerProcessType, setRetriggerProcessType] = useState('');
  const [retriggerProcessName, setRetriggerProcessName] = useState('');
  const [threadTags, setThreadTags] = useState([]);

  // API base URL
  const API_BASE_URL = '/pceapi/threads/unstructured';

  // HTTP headers with authorization
  const headers = {
    Authorization: GetCookie('access_token')
  };

  // Initialize searchInput with search value
  useEffect(() => {
    setSearchInput(search);
  }, []);

  // Watch for external refresh trigger
  useEffect(() => {
    if (externalRefreshTrigger > 0) {
      setRefreshTrigger(prev => prev + 1);
    }
  }, [externalRefreshTrigger]);

  // Fetch thread tags for classification dropdown
  useEffect(() => {
    if (!threadId) return;
    const fetchThreadTags = async () => {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/details/${threadId}`,
          { headers }
        );
        if (response.data.status === 'success' && response.data.data?.thread?.tags) {
          setThreadTags(response.data.data.thread.tags);
        }
      } catch (err) {
        console.error('Error fetching thread tags:', err);
      }
    };
    fetchThreadTags();
  }, [threadId]);

  // Fetch documents on component mount and when dependencies change
  useEffect(() => {
    fetchDocuments();
  }, [threadId, page, size, search, sortField, sortDirection, refreshTrigger]);

  // Replace your existing fetchDocuments function with this
const fetchDocuments = async () => {
  if (!threadId) return;
  
  setIsProcessingPopupVisible(true);
  setProcessingMessage('Loading documents');
  
  try {
    // Fetch both document status and metadata in parallel
    const [statusResponse, metadataResponse] = await Promise.all([
      axios.post(
        `${API_BASE_URL}/document-status`, 
        {
          thread_id: threadId,
          page,
          size,
          search: search || undefined,
          sort_field: sortField,
          sort_direction: sortDirection
        },
        { headers }
      ),
      axios.post(
        `${API_BASE_URL}/document-metadata`,
        {
          thread_id: threadId,
          page,
          size,
          search: search || undefined,
          sort_field: sortField,
          sort_direction: sortDirection,
        },
        { headers }
      )
    ]);

    // Handle status response
    const statusData = statusResponse.data;
    if (statusData.status === 'NotAuthorized') {
      setIsProcessingPopupVisible(false);
      authNavigate('/login', 'iam', navigate);
      return;
    }
    
    if (statusData.status === 'failed') {
      setIsProcessingPopupVisible(false);
      setPopupErrorMessage(statusData.message || 'Failed to load documents');
      setIsErrorPopupVisible(true);
      return;
    }
    
    setDocuments(statusData.data || []);
    setTotal(statusData.total || 0);
    setDocPrecentages(statusData.percent);

    // Handle metadata response
    const metadataData = metadataResponse.data;
    if (metadataData.status === 'success' || metadataData.status !== 'failed') {
      setMetadataList(metadataData.data || []);
      setMetadataTotal(metadataData.total || 0);
    }
    
    // If callback provided, notify parent that documents have been updated
    if (onDocumentsUpdated) {
      onDocumentsUpdated(statusData.data || []);
    }
  } catch (error) {
    setPopupErrorMessage(error.message || 'Failed to load documents');
    setIsErrorPopupVisible(true);
  } finally {
    setIsProcessingPopupVisible(false);
  }
};

  // Handle page change
  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  // Handle size change
  const handleSizeChange = (event) => {
    setSize(parseInt(event.target.value, 25));
    setPage(1); // Reset to first page when changing size
  };

  // Handle search input change
  const handleSearchInputChange = (event) => {
    setSearchInput(event.target.value);
  };

  // Handle search button click
  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1); // Reset to first page when search changes
  };
  
  // Handle Enter key press in search input
  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Handle search form submission
  const handleSearchSubmit = (event) => {
    event.preventDefault();
    handleSearch();
  };

  // Handle sort change
  const handleSortChange = (field) => {
    if (sortField === field) {
      // If clicking the same field, toggle direction
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // If clicking a new field, set it as sort field with default asc direction
      setSortField(field);
      setSortDirection('asc');
    }
    setPage(1); // Reset to first page when sorting
  };

  // Render sort arrow
  const renderSortArrow = (field) => {
    if (sortField !== field) return null;
    
    return (
      <span className="ms-1">
        {sortDirection === 'asc' ? 
          <i className="bi bi-caret-up-fill"></i> : 
          <i className="bi bi-caret-down-fill"></i>
        }
      </span>
    );
  };

  // Handle refresh button click
  const handleRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  // Close error popup
  const closeErrorPopup = () => {
    setIsErrorPopupVisible(false);
  };
  
  // Close success popup
  const closeSuccessPopup = () => {
    setIsSuccessPopupVisible(false);
  };
  
  // Show delete confirmation dialog
  const confirmDelete = (document) => {
    setDocumentToDelete(document);
    setShowDeleteConfirm(true);
  };
  
  const handleDocSelect = (document_id, to_add) => {
  let newSelectedDocuments;
  
  if (to_add === false) {
    newSelectedDocuments = selectedDocuments.filter(id => id !== document_id);
  } else {
    newSelectedDocuments = [...selectedDocuments, document_id];
  }
  
  setSelectedDocuments(newSelectedDocuments);
  
  // Update selectAll state based on whether all documents are selected
  const allDocIds = documents.map(doc => doc.id);
  const allSelected = allDocIds.every(id => newSelectedDocuments.includes(id)) && allDocIds.length > 0;
  setSelectAll(allSelected);
};

// Add this useEffect to reset selectAll when documents change (pagination, search, etc.)
useEffect(() => {
  const allDocIds = documents.map(doc => doc.id);
  const allSelected = allDocIds.every(id => selectedDocuments.includes(id)) && allDocIds.length > 0;
  setSelectAll(allSelected);
}, [documents, selectedDocuments]);

  // Handle delete selected documents
  const handleDeleteSelectedDocuments = async() => {
    setShowDeleteSelectedConfirm(false);
    if (selectedDocuments.length > 0) {
      setIsProcessingPopupVisible(true);
      setProcessingMessage(`Deleting ${selectedDocuments.length} selected documents`);

      try {
        const res = await axios.delete(
          `${API_BASE_URL}/delete-many-documents`,{headers,
          data : {
            doc_ids: selectedDocuments,
            thread_id: threadId
          }}
        );
      const data = res.data;
      
      if (data.status === 'NotAuthorized') {
        authNavigate('/login', 'iam', navigate);
        return;
      }
      
      if (data.status === 'success') {
        setPopupSuccessMessage(data.message || 'Documents deleted successfully');
        setIsSuccessPopupVisible(true)
      }
      if (data.status === 'failed') {
        setPopupErrorMessage(data.message || 'Failed to delete documents');
        setIsErrorPopupVisible(true);
      }
      
    }
    catch (error) {
      setPopupErrorMessage(error.message || 'Failed to load documents');
      setIsErrorPopupVisible(true);
    } finally {
      setSelectedDocuments([]);
      setIsProcessingPopupVisible(false);
      setRefreshTrigger(prev => prev +1) 
    }
  };
}

  // Handle single document deletion
  const handleDelete = async () => {
    setShowDeleteConfirm(false);
    
    if (!documentToDelete) return;
    
    setIsProcessingPopupVisible(true);
    setProcessingMessage(`Deleting document ${documentToDelete.document_name}`);
    
    try {
      const response = await axios.delete(
        `${API_BASE_URL}/delete-document`,
        { 
          headers,
          data: {
            document_id: documentToDelete.id,
            thread_id: threadId
          }
        }
      );
      
      const data = response.data;
      
      if (data.status === 'NotAuthorized') {
        setIsProcessingPopupVisible(false);
        authNavigate('/login', 'iam', navigate);
        return;
      }
      
      if (data.status === 'failed') {
        setIsProcessingPopupVisible(false);
        setPopupErrorMessage(data.message || 'Failed to delete document');
        setIsErrorPopupVisible(true);
        return;
      }
      
      setPopupSuccessMessage(data.message || 'Document deleted successfully');
      setIsSuccessPopupVisible(true);
      
      // Refresh the document list
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Error deleting document:', error);
      setPopupErrorMessage(error.message || 'Failed to delete document');
      setIsErrorPopupVisible(true);
    } finally {
      setIsProcessingPopupVisible(false);
      setDocumentToDelete(null);
    }
  };
  
  // Generate body content for delete modal
  const getDeleteModalBodyContent = () => {
    if (!documentToDelete) return null;
    
    return (
      <div>
        <p className="text-danger mb-0">This action cannot be undone.</p>
      </div>
    );
  };

  // Generate body content for delete selected modal
  const getDeleteSelectedModalBodyContent = () => {
    return (
      <div>
        <p className="mb-2">
          You are about to delete <strong>{selectedDocuments.length}</strong> selected documents.
        </p>
        <p className="text-danger mb-0">This action cannot be undone.</p>
      </div>
    );
  };

  // Pagination controls
  const totalPages = Math.ceil(total / size);
  const paginationItems = [];
  
  // Create pagination links
  const maxPagesToShow = 5;
  let startPage = Math.max(1, page - Math.floor(maxPagesToShow / 2));
  let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
  
  if (endPage - startPage + 1 < maxPagesToShow) {
    startPage = Math.max(1, endPage - maxPagesToShow + 1);
  }
  
  // Previous button
  paginationItems.push(
    <li key="prev" className={`page-item ${page === 1 ? 'disabled' : ''}`}>
      <button 
        className="ev-btn-light-fill me-1" 
        
        onClick={() => handlePageChange(page - 1)}
        disabled={page === 1}
      >
        <i className="bi bi-chevron-left"></i>
      </button>
    </li>
  );
  
  // Page numbers
  for (let i = startPage; i <= endPage; i++) {
    paginationItems.push(
      <li key={i} className={`page-item ${page === i ? 'active' : ''}`}>
        <button 
           className="ev-btn-light-fill me-1"
          onClick={() => handlePageChange(i)}
        >
          {i}
        </button>
      </li>
    );
  }
  
  // Next button
  paginationItems.push(
    <li key="next" className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
      <button 
         className="ev-btn-light-fill me-1" 
        onClick={() => handlePageChange(page + 1)}
        disabled={page === totalPages}
      >
        <i className="bi bi-chevron-right"></i>
      </button>
    </li>
  );

  const handlePreviewDocument = (doc_id, documentName) => {
    setPreviewDocId(doc_id);
    console.log("DOC ID");
    console.log(doc_id);
    setPreviewDocumentName(documentName);
    setPreviewOpen(true);
  };

  const closePreview = () => {
    setPreviewOpen(false);
    setPreviewDocId(null);
    setPreviewDocumentName(null);
  };
 const handleRetrigger = (processType) => {
  const processNames = {
    migration: "Migration",
    standardization: "Standardization",
    markdownrag: "Markdown RAG",
    imagerag: "Image RAG",
    autoclassifier: "AutoClassifier",
    parquet:"Parquet"
  };

  const processName = processNames[processType] || processType;
  
  setRetriggerProcessType(processType);
  setRetriggerProcessName(processName);
  setShowRetriggerConfirm(true);
};

const navigate = useNavigate();

const handleConfirmRetrigger = async () => {
  setShowRetriggerConfirm(false);
  
  if (!threadId) return;
  
  setIsProcessingPopupVisible(true);
  setProcessingMessage(`Triggering ${retriggerProcessName} process...`);

  try {
    const response = await axios.post(
      `${API_BASE_URL}/retrigger`,
      {
        thread_id: threadId,
        process_type: retriggerProcessType,
      },
      { headers }
    );

    const data = response.data;

    if (data.status === "NotAuthorized") {
      setIsProcessingPopupVisible(false);
      authNavigate('/login', 'iam', navigate);
      return;
    }

    if (data.status === "failed") {
      setIsProcessingPopupVisible(false);
      setPopupErrorMessage(`Failed to trigger ${retriggerProcessName} process`);
      setIsErrorPopupVisible(true);
      return;
    }

    setPopupSuccessMessage(`${retriggerProcessName} process triggered successfully`);
    setIsSuccessPopupVisible(true);

    // Refresh the document list after a short delay
    setTimeout(() => {
      setRefreshTrigger((prev) => prev + 1);
    }, 2000);

  } catch (error) {
    console.error(`Error triggering ${retriggerProcessName} process:`, error);
    setPopupErrorMessage(`Failed to trigger ${retriggerProcessName} process`);
    setIsErrorPopupVisible(true);
  } finally {
    setIsProcessingPopupVisible(false);
  }
};

  const handleSelectAllToggle = () => {
  const newSelectAll = !selectAll;
  setSelectAll(newSelectAll);
  
  if (newSelectAll) {
    // Select all current page documents
    const allDocIds = documents.map(doc => doc.id);
    setSelectedDocuments(allDocIds);
  } else {
    // Deselect all
    setSelectedDocuments([]);
  }
};

  return (
    <div className="document-status-table mt-5">
      {/* Toggle Button */}
      <div className="mb-3 text-end">
        <div className="btn-group custom-btn-group-outer">
          <button
            className={`btn custom-btn-group-inner ${
              currentView === "status" ? 'ev-btn-blue-fill-round' : 'ev-btn-lightblue-fill-round'
            }`}
            onClick={() => setCurrentView("status")}
          >
            <i className="bi bi-list-check"></i> Document Status
          </button>
          <button
            className={`btn custom-btn-group-inner ${
              currentView === "metadata" ? 'ev-btn-blue-fill-round' : 'ev-btn-lightblue-fill-round'
            }`}
            onClick={() => setCurrentView("metadata")}
          >
            <i className="bi bi-pencil-square"></i> Metadata Editor
          </button>
        </div>
      </div>

      {/* Render based on current view */}
      {currentView === "status" ? (
        <>
          {/* Document Status View */}
          <div className="row mt-1 mb-3">
            <div className="col-6 d-flex align-items-center position-relative">
              <p className="ev-reg-p-blue mb-0 w-20">Documents ({total})</p>

              {/* Search form */}
              <div className="w-60"> 
              <input
                type="text"
                className="form-control search-input ms-0"
                placeholder="Search document name"
                value={searchInput}
                onChange={handleSearchInputChange}
                onKeyPress={handleSearchKeyPress}
              />
              <button 
                className="ev-btn-lightblue-fill-round search-bar-button" 
                onClick={handleSearch}
              >
                <i className="bi bi-search"></i>
              </button>
              </div>
            </div>
            
            <div className="col-6 text-end">
              <button 
                className="ev-btn-red-fill-round" 
                disabled={selectedDocuments.length === 0}
                onClick={() => setShowDeleteSelectedConfirm(true)}
              >
                Delete selected ({selectedDocuments.length})
              </button>
              <button 
                className="ev-btn-lightblue-fill-round ev-bold-reg ms-2"  
                onClick={handleRefresh}
              >
                <i className="bi bi-arrow-clockwise"></i> Refresh Documents
              </button>
            </div>
          </div>
          
          {documents.length === 0 && !isProcessingPopupVisible ? (
            <div className="alert alert-primary">No documents found in this Data Collection.</div>
          ) : (
            <>
              <div className="table-container">
                <table className="table-custom1 mt-0 overflow-hidden w-100 table-fixed">
                  <colgroup>
                    <col className="col-status-check" />
                    <col className="col-status-name" />
                    <col className="col-status-size" />
                    <col className="col-status-upload" />
                    <col className="col-status-stand" />
                    <col className="col-status-mdrag" />
                    <col className="col-status-imgrag" />
                    <col className="col-status-auto" />
                    <col className="col-status-parquet" />
                    <col className="col-status-actions" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th></th>
                      <th></th>
                      <th></th>
                      <th></th>
                      <th className="p-0">
                        <button
                         className="ev-btn-blue-outline-round ev-btn-xsmall ev-bold-reg"
                         onClick={() => handleRetrigger('standardization')}
                       >
                         <i className="bi bi-arrow-clockwise"></i> Retrigger
                       </button>
                     </th>
                     <th  className="p-0">
                       <button
                         className="ev-btn-blue-outline-round ev-btn-xsmall ev-bold-reg"
                         onClick={() => handleRetrigger('markdownrag')}
                       >
                         <i className="bi bi-arrow-clockwise"></i> Retrigger
                       </button>
                     </th>
                     <th className="p-0">
                       <button
                         className="ev-btn-blue-outline-round ev-btn-xsmall ev-bold-reg"
                         onClick={() => handleRetrigger('imagerag')}
                       >
                         <i className="bi bi-arrow-clockwise"></i> Retrigger
                       </button>
                     </th>
                     <th className="p-0">
                       <button
                         className="ev-btn-blue-outline-round ev-btn-xsmall ev-bold-reg"
                         onClick={() => handleRetrigger('autoclassifier')}
                       >
                         <i className="bi bi-arrow-clockwise"></i> Retrigger
                       </button>
                     </th>
                     <th className="p-0">
                       <button
                         className="ev-btn-blue-outline-round ev-btn-xsmall ev-bold-reg"
                         onClick={() => handleRetrigger('parquet')}
                       >
                         <i className="bi bi-arrow-clockwise"></i> Retrigger
                       </button>
                     </th>
                     <th></th>
                   </tr>
                   <tr className="cursor-pointer">
                     <th>
                       <input 
                         type="checkbox" 
                         checked={selectAll}
                         onChange={handleSelectAllToggle}
                         disabled={documents.length === 0}
                       />
                     </th>
                     <th onClick={() => handleSortChange('document_name')}>
                       <label>Document Name {renderSortArrow('document_name')}</label>
                     </th>
                     <th onClick={() => handleSortChange('document_size_mb')}>
                       <label>Size (MB) {renderSortArrow('document_size_mb')}</label>
                     </th>
                     <th onClick={() => handleSortChange('upload_status')}>
                       <label>Upload {renderSortArrow('upload_status')}<br/>{docPercentages.upl_sta}%</label>
                     </th>
                     <th onClick={() => handleSortChange('standardization_status')}>
                       <label>Standardization {renderSortArrow('standardization_status')}<br/>{docPercentages.stand_sta}%</label>
                     </th>
                     <th onClick={() => handleSortChange('markdown_rag_status')}>
                       <label>Markdown RAG {renderSortArrow('markdown_rag_status')}<br/>{docPercentages.md_rag_sta}%</label>
                     </th>
                     <th onClick={() => handleSortChange('image_rag_status')}>
                       <label>Image RAG {renderSortArrow('image_rag_status')}<br/>{docPercentages.img_rag_sta}%</label>
                     </th>
                     <th onClick={() => handleSortChange('autoclassification_status')}>
                       <label>AutoClassifer {renderSortArrow('autoclassification_status')}<br/>{docPercentages.auto_class_sta}%</label>
                     </th>
                     <th onClick={() => handleSortChange('parquet_status')}>
                       <label>Parquet {renderSortArrow('parquet_status')}<br/>{docPercentages.parquet_sta}%</label>
                     </th>
                     <th>
                       <label>Actions</label>
                     </th>
                   </tr>
                  </thead>
                </table>

                <div className="scrollable-element vh-75">
                  <table className="table-custom1 overflow-hidden w-100 table-fixed">
                    <colgroup>
                      <col className="col-status-check" />
                      <col className="col-status-name" />
                      <col className="col-status-size" />
                      <col className="col-status-upload" />
                      <col className="col-status-stand" />
                      <col className="col-status-mdrag" />
                      <col className="col-status-imgrag" />
                      <col className="col-status-auto" />
                      <col className="col-status-parquet" />
                      <col className="col-status-actions" />
                    </colgroup>
                    <tbody>
                      {documents.map((doc, index) => (
                        <tr key={index}>
                          <td className="align-top">
                            <CheckBoxForDocSelect doc_id={doc.id} checked={selectedDocuments.includes(doc.id)} onChange={handleDocSelect} />
                          </td>
                          <td className="align-top">
                            <div className="d-flex j align-items-start">
                              <div className="d-flex flex-column">
                                <div className="avatar-tooltip" data-tooltip={doc.document_name || 'Uploading...'}>
                                  <TrimContent text={doc.document_name || 'Uploading...'} maxLength={20} />
                                </div>
                                
                                {/* New AI Legality Badge */}
                                {(doc.document_type === 'pdf' || doc.document_type === 'docx' || doc.document_type === 'txt') && (
                                  <AILegalityBadge
                                    status={doc.ai_legality_status}
                                    reason={doc.ai_legality_reason}
                                    isOpen={activePopoverId === index}
                                    onToggle={() => setActivePopoverId(activePopoverId === index ? null : index)}
                                  />
                                )}
                              </div>

                              <button 
                                onClick={() => handlePreviewDocument(doc.id, doc.document_name || 'Unnamed Document')} 
                                className="ev-btn-lightblue-fill-round ev-btn-xsmall ms-2 text-truncate align-self-start">
                                <i className="bi bi-eye"></i> view 
                              </button> 
                            </div>
                          </td>
                          <td className="align-top">{doc.document_size_mb.toFixed(2)}</td>
                          <td className="align-top"><StatusBadge status={doc.upload_status} fullWidth={true} /></td>
                          <td className="align-top"><StatusBadge status={doc.standardization_status} fullWidth={true} /></td>
                          <td className="align-top"><StatusBadge status={doc.markdown_rag_status} fullWidth={true} /></td>
                          <td className="align-top"><StatusBadge status={doc.image_rag_status} fullWidth={true} /></td>
                          <td className="align-top"><StatusBadge status={doc.autoclassification_status} fullWidth={true} /></td>
                          <td className="align-top"><StatusBadge status={doc.parquet_status} fullWidth={true} /></td>
                          <td className="align-top text-center">
                            <button
                              className="btn ev-btn-red-fill-round ev-btn-med ev-radius-xsmall pe-2 ps-2 avatar-tooltip" 
                              onClick={() => confirmDelete(doc)}
                              data-tooltip="Delete"
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-3">
                  {/* Items per page selector */}
                  <div>
                <PaginationControls 
                  initialSize={size}
                  onSizeChange={(newSize) => {
                    setSize(newSize);
                    setPage(1);
                  }}
                />
            </div>
                  <div className="ev-small-p-dark"> 
                    Showing {Math.min((page - 1) * size + 1, total)} to {Math.min(page * size, total)} of {total} documents
                  </div>
                  <nav aria-label="Document pagination">
                    <ul className="pagination pagination-sm mb-0 me-w">
                      {paginationItems}
                    </ul>
                  </nav>
                </div>
              )}
            </>
          )}

        </>
        ) : (
          /* Metadata Editor View */
          <MetadataEditor
            threadId={threadId}
            documents={documents}
            metadataList={metadataList}
            metadataTotal={metadataTotal}
            page={page}
            size={size}
            search={search}
            setSize={setSize}
            sortField={sortField}
            sortDirection={sortDirection}
            onDocumentsUpdated={onDocumentsUpdated}
            onPreviewDocument={handlePreviewDocument}
            onPageChange={handlePageChange}
            onSizeChange={handleSizeChange}
            onSearchChange={setSearch}
            onSortChange={handleSortChange}
            onRefresh={handleRefresh}
            editingRows={metadataEditingRows}
            setEditingRows={setMetadataEditingRows}
            editedMetadata={metadataEditedData}
            setEditedMetadata={setMetadataEditedData}
            hasGlobalChanges={metadataHasGlobalChanges}
            setHasGlobalChanges={setMetadataHasGlobalChanges}
            // Pass popup handlers
            setIsProcessingPopupVisible={setIsProcessingPopupVisible}
            setProcessingMessage={setProcessingMessage}
            setIsErrorPopupVisible={setIsErrorPopupVisible}
            setPopupErrorMessage={setPopupErrorMessage}
            setIsSuccessPopupVisible={setIsSuccessPopupVisible}
            setPopupSuccessMessage={setPopupSuccessMessage}
            threadTags={threadTags}
          />
        )}

        {/* Document Preview Component */}
        <DocumentPreview
          isOpen={previewOpen}
          onClose={closePreview}
          docId={previewDocId}
          documentName={previewDocumentName}
        />

        {/* Popups */}
        {isProcessingPopupVisible && (
          <ProcessingPopup message={processingMessage} />
        )}
        
        {isErrorPopupVisible && (
          <ErrorPopup 
            onClose={closeErrorPopup}
            message={popupErrorMessage}
          />
        )}
        
        {isSuccessPopupVisible && (
          <SuccessPopup 
            onClose={closeSuccessPopup}
            message={popupSuccessMessage}
          />
        )}
        
        {/* Single Document Delete Confirmation Modal */}
        <DeleteConfirmationModal
          show={showDeleteConfirm}
          onClose={() => {
            setShowDeleteConfirm(false);
            setDocumentToDelete(null);
          }}
          onConfirm={handleDelete}
          objectName="document"
          itemName={documentToDelete?.document_name}
          bodyContent={getDeleteModalBodyContent()}
        />

        {/* Multiple Documents Delete Confirmation Modal */}
        <DeleteConfirmationModal
          show={showDeleteSelectedConfirm}
          onClose={() => {
            setShowDeleteSelectedConfirm(false);
            setSelectedDocuments([]);
          }}
          onConfirm={handleDeleteSelectedDocuments}
          objectName="documents"
          itemName={`${selectedDocuments.length} selected documents`}
          bodyContent={getDeleteSelectedModalBodyContent()}
        />

        {/* Retrigger Confirmation Modal */}
        <RetriggerConfirmationModal
          show={showRetriggerConfirm}
          onClose={() => setShowRetriggerConfirm(false)}
          onConfirm={handleConfirmRetrigger}
          processName={retriggerProcessName}
          bodyContent={
            <p className="text-info">
              This will restart the process for all documents in this Data Collection which are not marked as COMPLETED.
            </p>
          }
        />
      </div>
    );
  };

  export default DocumentStatusTable;